import { Box, Button, Tooltip, Typography, useTheme } from "@mui/material"
import React, { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useSnackbarContext } from "../services/snackbar"
import { Loading } from "../components/Loading"
import { useAuthContext } from "../services/auth"
import { UserRole } from "../services/auth/auth.types"
import { FormatParagraphs } from "../components/Util"
import { PlaylistQueryType, PlaylistType } from "../services/api/playlist"
import { usePlaylistAPI } from "../services/api/hooks/usePlaylistAPI"
import { VideoSuggestions } from "../components/VideoSuggestions"
import { VideoQueryType } from "../services/api/video"
import { httpClient } from "../services/http"
import { useStaticContext } from "../services/static"

import { ReactComponent as FeatherIcon } from "../assets/svg/feather.svg"
import { ReactComponent as StarIcon } from "../assets/svg/star.svg"

export const Playlist = (): React.ReactElement => {
  const [searchParams] = useSearchParams()
  const playlistCode = searchParams.get("pl") || ""
  const { playlistsData, loading, error, refresh } = usePlaylistAPI({
    constraint: PlaylistQueryType.PLAYLIST,
    code: playlistCode,
  })
  const { open: sbOpen } = useSnackbarContext()
  const navigate = useNavigate()
  const { role, userId } = useAuthContext()
  const theme = useTheme()
  const { playlists, reloadPlaylists } = useStaticContext()

  const [playlist, setPlaylist] = useState<PlaylistType>()

  const saved = playlists.some((pl) => pl.code === playlistCode)

  const handleSave = async () => {
    if (!playlist) return
    try {
      if (saved) {
        await httpClient.instance.delete(`/library/${playlist.code}`)
        sbOpen("Playlist supprimée de votre bibliothèque", "info")
      } else {
        await httpClient.instance.put(`/library/${playlist.code}`)
        sbOpen("Playlist ajoutée à votre bibliothèque", "success")
      }
    } catch (e) {
      sbOpen("Une erreur s'est produite", "error")
      console.error(e)
    } finally {
      reloadPlaylists()
    }
  }

  useEffect(() => {
    refresh()
  }, [playlistCode])

  useEffect(() => {
    if (playlistsData && playlistsData[0]) {
      setPlaylist(playlistsData[0])
    }
    if (error) {
      sbOpen("Une erreur s'est produite", "error")
      navigate("/")
    }
  }, [playlistCode, loading, playlistsData, error])

  if (error) {
    sbOpen("Une erreur s'est produite", "error")
    navigate("/")
  }

  return !loading && playlist ? (
    <Box sx={{ position: "relative", zIndex: 200 }}>
      <Box sx={{ backgroundColor: "none" }} py={4}>
        <Box display={"flex"} justifyContent={"center"} flexDirection={"row"} alignItems={"start"} px={4}>
          <Box
            sx={{
              [theme.breakpoints.up("lg")]: { maxWidth: "50%" },
              paddingX: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <Typography component="h1" variant="h1" fontSize={"3rem"} pt={2}>
              {playlist.title}
              {playlist.authorId === userId && (
                <Box ml={1} component={"span"} display="inline">
                  <Tooltip title={"Créée par vous"}>
                    <FeatherIcon
                      width={"auto"}
                      height={"1.3rem"}
                      fill={theme.palette.mode === "dark" ? "white" : "black"}
                    />
                  </Tooltip>
                </Box>
              )}
              {playlist.isPublic && (
                <Box ml={1} component={"span"} display="inline">
                  <Tooltip title={"Featurée"}>
                    <StarIcon width={"auto"} height={"1.3rem"} />
                  </Tooltip>
                </Box>
              )}
            </Typography>
            <Typography mt={4}>
              <FormatParagraphs content={playlist.description} />
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ minHeight: "300px" }} px={"10%"}>
        {(playlist.authorId === userId || role === UserRole.GEEK) && (
          <Button
            sx={{ margin: 1 }}
            variant="outlined"
            color="secondary"
            onClick={() => navigate(`/editPlaylist/${playlist.code}`)}
          >
            {"Modifier"}
          </Button>
        )}
        {playlist.authorId !== userId &&
          (saved ? (
            <Button sx={{ margin: 1 }} variant="outlined" color="error" onClick={handleSave}>
              {"Retirer de ma bibliothèque"}
            </Button>
          ) : (
            <Button sx={{ margin: 1 }} variant="contained" color="secondary" onClick={handleSave}>
              {"Ajouter à ma bibliothèque"}
            </Button>
          ))}
        {
          <VideoSuggestions
            query={{ constraint: VideoQueryType.PLAYLIST, code: playlist.code }}
            playlistCode={playlist.code}
            md={3}
          />
        }
      </Box>
    </Box>
  ) : (
    <Loading />
  )
}
